import { getScribeHttpClients } from '@heidi/api/client';

// ============================================================================
// Transport
// ============================================================================

interface ScribeHttp {
  post<T>(url: string, body?: unknown): Promise<{ data: T }>;
  delete<T>(url: string): Promise<{ data: T }>;
}

// Resolved PER CALL: the registry is filled at app bootstrap.
const http = (): ScribeHttp => getScribeHttpClients().scribeHttpV2;

// ============================================================================
// By ID helpers (used when the source ID is already known)
// ============================================================================

/** Mark a source as read by its ID and optionally track actual read time. Best-effort. */
export async function markSourceAsRead(
  sourceId: string,
  actualReadTimeSeconds?: number
): Promise<void> {
  try {
    await http().post(`/cpd/sources/${sourceId}/read`, {
      actual_read_time_seconds: actualReadTimeSeconds ?? null,
    });
  } catch {
    // Best-effort tracking
  }
}

/** Save a source for later by its ID. Returns true on success. */
export async function saveSource(sourceId: string): Promise<boolean> {
  try {
    await http().post(`/cpd/sources/${sourceId}/save`);
    return true;
  } catch {
    return false;
  }
}

// ============================================================================
// By URL helpers (used from chat/evidence where only URL + title are known)
// ============================================================================

/**
 * Response from the ensure-and-save / ensure-and-read endpoints.
 */
interface EnsureAndActionResponse {
  source_id: string;
  saved?: boolean;
  marked_read?: boolean;
  created: boolean;
  message: string;
}

/**
 * Marks a CPD source as read by its URL.
 *
 * Uses the `read-by-url` endpoint which ensures the source exists
 * (processing pending CPD events and creating on-demand if needed)
 * before marking it as read. This eliminates the race condition where
 * the user clicks "mark as read" before async CPD processing completes.
 *
 * @param url - The source URL
 * @param title - The source title (used for enrichment if source must be created)
 * @param actualReadTimeSeconds - Optional actual reading time from the viewer timer
 * @returns The source_id on success, null on failure
 */
export async function markSourceReadByUrl(
  url: string,
  title?: string,
  actualReadTimeSeconds?: number
): Promise<string | null> {
  try {
    const { data } = await http().post<EnsureAndActionResponse>(
      '/cpd/sources/read-by-url',
      {
        url,
        title: title || url,
        actual_read_time_seconds: actualReadTimeSeconds ?? null,
      }
    );
    return data.source_id;
  } catch {
    return null;
  }
}

/**
 * Saves a CPD source for later by its URL.
 *
 * Uses the `save-by-url` endpoint which ensures the source exists
 * (processing pending CPD events and creating on-demand if needed)
 * before saving it. This eliminates the race condition where the user
 * clicks "save for later" before async CPD processing completes.
 *
 * @param url - The source URL
 * @param title - The source title (used for enrichment if source must be created)
 * @returns The source_id on success, null on failure
 */
export async function saveSourceByUrl(
  url: string,
  title?: string
): Promise<string | null> {
  try {
    const { data } = await http().post<EnsureAndActionResponse>(
      '/cpd/sources/save-by-url',
      {
        url,
        title: title || url,
      }
    );
    return data.source_id;
  } catch {
    return null;
  }
}

// ============================================================================
// Undo helpers (used for toggling read/saved state in citation tooltips)
// ============================================================================

/** Marks a CPD source as unread by its ID. Best-effort. */
export async function markSourceUnreadById(sourceId: string): Promise<boolean> {
  try {
    await http().delete(`/cpd/sources/${sourceId}/read`);
    return true;
  } catch {
    return false;
  }
}

/** Removes a CPD source from saved list by its ID. Best-effort. */
export async function unsaveSourceById(sourceId: string): Promise<boolean> {
  try {
    await http().delete(`/cpd/sources/${sourceId}/save`);
    return true;
  } catch {
    return false;
  }
}
